import { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Button,
  Box,
  Container,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import { motion } from 'framer-motion';
import BrandLogo from './BrandLogo';

const navLinks = [
  { label: 'Home', href: '#home' },
  { label: 'Portfolio', href: '#portfolio' },
  { label: 'Services', href: '#services' },
  { label: 'About', href: '#about' },
  { label: 'Contact', href: '#contact' },
];

export default function Navbar() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (href) => {
    setDrawerOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      <AppBar
        position='fixed'
        elevation={0}
        sx={{
          background: scrolled ? 'rgba(15, 15, 26, 0.85)' : 'transparent',
          backdropFilter: scrolled ? 'blur(20px)' : 'none',
          WebkitBackdropFilter: scrolled ? 'blur(20px)' : 'none',
          borderBottom: scrolled ? '1px solid rgba(255,255,255,0.08)' : 'none',
          transition: 'all 0.4s ease',
        }}
      >
        <Container maxWidth='lg'>
          <Toolbar disableGutters sx={{ py: 1 }}>
            {/* Logo */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              style={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  cursor: 'pointer',
                }}
                onClick={() => handleNavClick('#home')}
              >
                <BrandLogo compact />
              </Box>
            </motion.div>

            {/* Desktop Nav */}
            {!isMobile && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                style={{ display: 'flex', alignItems: 'center', gap: 8 }}
              >
                {navLinks.map((link) => (
                  <Button
                    key={link.label}
                    onClick={() => handleNavClick(link.href)}
                    sx={{
                      color: 'rgba(255,255,255,0.75)',
                      fontWeight: 500,
                      fontSize: '0.9rem',
                      px: 2,
                      borderRadius: '8px',
                      '&:hover': {
                        color: '#fff',
                        background: 'rgba(124,58,237,0.12)',
                      },
                    }}
                  >
                    {link.label}
                  </Button>
                ))}
                <Button
                  variant='contained'
                  onClick={() => handleNavClick('#contact')}
                  sx={{
                    ml: 1,
                    background: 'linear-gradient(135deg, #F59E0B, #D97706)',
                    color: '#000',
                    fontWeight: 700,
                    boxShadow: '0 4px 15px rgba(245,158,11,0.35)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #FBBF24, #F59E0B)',
                      boxShadow: '0 6px 20px rgba(245,158,11,0.5)',
                      transform: 'translateY(-2px)',
                    },
                  }}
                >
                  Hire Me
                </Button>
              </motion.div>
            )}

            {/* Mobile hamburger */}
            {isMobile && (
              <IconButton
                onClick={() => setDrawerOpen(true)}
                sx={{ color: '#fff' }}
              >
                <MenuIcon />
              </IconButton>
            )}
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        anchor='right'
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: {
            width: 280,
            background: 'rgba(15, 15, 26, 0.97)',
            backdropFilter: 'blur(20px)',
            borderLeft: '1px solid rgba(255,255,255,0.08)',
            pt: 2,
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            px: 2,
            pb: 2,
          }}
        >
          <BrandLogo compact iconSize={34} />
          <IconButton
            onClick={() => setDrawerOpen(false)}
            sx={{ color: '#fff' }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <List>
          {navLinks.map((link) => (
            <ListItem key={link.label} disablePadding>
              <ListItemButton
                onClick={() => handleNavClick(link.href)}
                sx={{
                  px: 3,
                  py: 1.5,
                  '&:hover': { background: 'rgba(124,58,237,0.15)' },
                }}
              >
                <ListItemText
                  primary={link.label}
                  primaryTypographyProps={{ fontWeight: 500 }}
                />
              </ListItemButton>
            </ListItem>
          ))}
          <ListItem sx={{ px: 2, pt: 2 }}>
            <Button
              fullWidth
              onClick={() => handleNavClick('#contact')}
              sx={{
                background: 'linear-gradient(135deg, #F59E0B, #D97706)',
                color: '#000',
                fontWeight: 700,
                py: 1.5,
                borderRadius: '10px',
              }}
            >
              Hire Me
            </Button>
          </ListItem>
        </List>
      </Drawer>
    </>
  );
}
